import { Controller } from "@hotwired/stimulus"
import { logger } from "../logger"

// Connects to data-controller="sidebar"
export default class extends Controller {
  static targets = ["toggleIcon", "backdrop", "submenuAdmin", "submenuUser", "chevronAdmin", "chevronUser", "sidebar"]
  static values = { collapsed: Boolean }

  connect() {
    this.updateToggleIcon()
    this.updateSidebarAwareElements()
  }

  // Toggle sidebar collapsed/expanded state (desktop)
  toggle(event) {
    event?.preventDefault()
    this.collapsedValue = !this.collapsedValue
    const sidebar = this.element.querySelector('.app-sidebar')
    const toggleButton = this.element.querySelector('.sidebar-toggle-btn')

    sidebar.classList.toggle('collapsed')
    toggleButton.classList.toggle('collapsed') // Also toggle on button for positioning

    this.updateToggleIcon()
    this.updateSidebarAwareElements()
    this.savePreference()
  }

  // Toggle mobile sidebar visibility
  toggleMobile(event) {
    event?.preventDefault()
    const sidebar = this.element.querySelector('.app-sidebar')
    sidebar.classList.toggle('mobile-open')
    this.updateBackdrop()
  }

  // Close mobile sidebar
  closeMobile(event) {
    event?.preventDefault()
    const sidebar = this.element.querySelector('.app-sidebar')
    sidebar.classList.remove('mobile-open')
    this.updateBackdrop()
  }

  // Toggle submenu on click (when expanded)
  toggleSubmenu(event) {
    event?.preventDefault()
    const submenuId = event.currentTarget.dataset.sidebarSubmenuParam

    if (!submenuId) return

    // Get the submenu element
    const submenuTarget = `submenu${submenuId.charAt(0).toUpperCase() + submenuId.slice(1)}`
    const chevronTarget = `chevron${submenuId.charAt(0).toUpperCase() + submenuId.slice(1)}`

    if (!this.hasTarget(submenuTarget)) return

    const submenu = this[`${submenuTarget}Target`]
    const sidebar = this.element.querySelector('.app-sidebar')

    // Only handle click when sidebar is expanded
    if (sidebar && !sidebar.classList.contains('collapsed')) {
      // Toggle inline submenu
      submenu.classList.toggle('show')

      // Rotate chevron icon if exists
      if (this.hasTarget(chevronTarget)) {
        const chevron = this[`${chevronTarget}Target`]
        chevron.classList.toggle('rotated')
      }
    }
    // When collapsed, ignore click (hover handles it)
  }

  // Open submenu on hover (only when collapsed)
  openSubmenu(event) {
    const submenuId = event.currentTarget.dataset.sidebarSubmenuParam

    if (!submenuId) return

    // Get the submenu element
    const submenuTarget = `submenu${submenuId.charAt(0).toUpperCase() + submenuId.slice(1)}`

    if (!this.hasTarget(submenuTarget)) return

    const submenu = this[`${submenuTarget}Target`]
    const sidebar = this.element.querySelector('.app-sidebar')

    // Only handle hover when sidebar is collapsed
    if (sidebar && sidebar.classList.contains('collapsed')) {
      // Cancel any scheduled close
      if (this.closeFloatingMenuTimeout) {
        clearTimeout(this.closeFloatingMenuTimeout)
        this.closeFloatingMenuTimeout = null
      }

      // Show as floating dropdown
      this.showFloatingSubmenu(submenu, event.currentTarget)
    }
    // When expanded, ignore hover (click handles it)
  }

  // Close submenu when mouse leaves (only when collapsed)
  closeSubmenu(event) {
    const submenuId = event.currentTarget.dataset.sidebarSubmenuParam

    if (!submenuId) return

    const sidebar = this.element.querySelector('.app-sidebar')

    // Only handle hover-close when sidebar is collapsed
    if (sidebar && sidebar.classList.contains('collapsed')) {
      this.scheduleCloseFloatingSubmenu()
    }
    // When expanded, ignore mouseleave (click handles toggle)
  }

  // Show submenu as floating dropdown when sidebar is collapsed
  showFloatingSubmenu(submenu, trigger) {
    // Close any existing floating submenu
    this.closeFloatingSubmenu()

    // Clone the submenu
    const floatingMenu = submenu.cloneNode(true)
    floatingMenu.classList.add('sidebar-floating-submenu', 'show')
    floatingMenu.classList.remove('sidebar-submenu')

    // Position it next to the trigger
    const triggerRect = trigger.getBoundingClientRect()
    const isRTL = document.dir === 'rtl' ||
                  document.documentElement.dir === 'rtl' ||
                  document.body.dir === 'rtl' ||
                  getComputedStyle(document.body).direction === 'rtl'

    // Add to body first (needed to measure actual height)
    document.body.appendChild(floatingMenu)
    this.currentFloatingMenu = floatingMenu
    this.currentFloatingMenuTrigger = trigger

    // Measure the menu's actual height
    const menuHeight = floatingMenu.offsetHeight
    const viewportHeight = window.innerHeight
    const spaceBelow = viewportHeight - triggerRect.top
    const spaceAbove = triggerRect.top

    // Determine vertical position
    let topPosition = triggerRect.top

    if (menuHeight > spaceBelow) {
      // Not enough space below
      if (spaceAbove > spaceBelow && menuHeight <= spaceAbove) {
        // More space above and menu fits - open upward
        // Align bottom of menu with bottom of trigger (not top)
        topPosition = triggerRect.bottom - menuHeight
      } else if (menuHeight > viewportHeight - 20) {
        // Menu taller than viewport - make scrollable and position at top with margin
        floatingMenu.style.maxHeight = `${viewportHeight - 40}px`
        floatingMenu.style.overflowY = 'auto'
        topPosition = 20
      } else {
        // Position to fit in viewport
        topPosition = Math.max(20, viewportHeight - menuHeight - 20)
      }
    }

    // Position horizontally (left/right of sidebar)
    if (isRTL) {
      floatingMenu.style.right = `${window.innerWidth - triggerRect.left}px`
      floatingMenu.style.left = 'auto'
    } else {
      floatingMenu.style.left = `${triggerRect.right}px`
      floatingMenu.style.right = 'auto'
    }
    floatingMenu.style.top = `${topPosition}px`

    // Keep menu open when hovering over it
    floatingMenu.addEventListener('mouseenter', this.floatingMenuMouseEnter = () => {
      if (this.closeFloatingMenuTimeout) {
        clearTimeout(this.closeFloatingMenuTimeout)
        this.closeFloatingMenuTimeout = null
      }
    })

    floatingMenu.addEventListener('mouseleave', this.floatingMenuMouseLeave = () => {
      this.scheduleCloseFloatingSubmenu()
    })

    // Also handle clicks to close
    setTimeout(() => {
      document.addEventListener('click', this.floatingMenuClickOutside = (e) => {
        if (!floatingMenu.contains(e.target) && !trigger.contains(e.target)) {
          this.closeFloatingSubmenu()
        }
      })
    }, 10)
  }

  // Schedule closing of floating submenu with a small delay
  scheduleCloseFloatingSubmenu() {
    if (this.closeFloatingMenuTimeout) {
      clearTimeout(this.closeFloatingMenuTimeout)
    }
    this.closeFloatingMenuTimeout = setTimeout(() => {
      this.closeFloatingSubmenu()
    }, 200) // 200ms delay to allow moving mouse to submenu
  }

  // Close floating submenu
  closeFloatingSubmenu() {
    if (this.closeFloatingMenuTimeout) {
      clearTimeout(this.closeFloatingMenuTimeout)
      this.closeFloatingMenuTimeout = null
    }
    if (this.currentFloatingMenu) {
      if (this.floatingMenuMouseEnter) {
        this.currentFloatingMenu.removeEventListener('mouseenter', this.floatingMenuMouseEnter)
        this.floatingMenuMouseEnter = null
      }
      if (this.floatingMenuMouseLeave) {
        this.currentFloatingMenu.removeEventListener('mouseleave', this.floatingMenuMouseLeave)
        this.floatingMenuMouseLeave = null
      }
      this.currentFloatingMenu.remove()
      this.currentFloatingMenu = null
    }
    if (this.currentFloatingMenuTrigger) {
      this.currentFloatingMenuTrigger = null
    }
    if (this.floatingMenuClickOutside) {
      document.removeEventListener('click', this.floatingMenuClickOutside)
      this.floatingMenuClickOutside = null
    }
  }

  // Update toggle icon direction
  updateToggleIcon() {
    if (!this.hasToggleIconTarget) return

    const icon = this.toggleIconTarget
    const isRTL = document.dir === 'rtl' ||
                  document.documentElement.dir === 'rtl' ||
                  document.body.dir === 'rtl' ||
                  getComputedStyle(document.body).direction === 'rtl'

    if (this.collapsedValue) {
      // When collapsed, arrow points toward content (to expand)
      // LTR: sidebar on left, points right →
      // RTL: sidebar on right, points left ←
      icon.className = isRTL ? 'fas fa-chevron-left' : 'fas fa-chevron-right'
    } else {
      // When expanded, arrow points into sidebar (to collapse)
      // LTR: sidebar on left, points left ←
      // RTL: sidebar on right, points right →
      icon.className = isRTL ? 'fas fa-chevron-right' : 'fas fa-chevron-left'
    }
  }

  // Update backdrop visibility for mobile
  updateBackdrop() {
    if (!this.hasBackdropTarget) return

    const backdrop = this.backdropTarget
    const sidebar = this.element.querySelector('.app-sidebar')
    const isMobileOpen = sidebar && sidebar.classList.contains('mobile-open')
    backdrop.classList.toggle('show', isMobileOpen)
  }

  // Update all sidebar-aware elements at once
  // Any element with class 'sidebar-aware' will have 'sidebar-collapsed' toggled
  updateSidebarAwareElements() {
    const sidebarAwareElements = document.querySelectorAll('.sidebar-aware')
    
    sidebarAwareElements.forEach(element => {
      if (this.collapsedValue) {
        element.classList.add('sidebar-collapsed')
      } else {
        element.classList.remove('sidebar-collapsed')
      }
    })

    // Special case: secondary sidebar uses different class name
    const secondarySidebar = document.querySelector('.app-secondary-sidebar')
    if (secondarySidebar) {
      if (this.collapsedValue) {
        secondarySidebar.classList.add('main-sidebar-collapsed')
      } else {
        secondarySidebar.classList.remove('main-sidebar-collapsed')
      }
    }

    // Notify other code that sidebar-aware elements have been updated so
    // they can react (for example, recalculate overlays / heights).
    // Other controllers can listen for the `sidebar:updated` event.
    try {
      document.dispatchEvent(new CustomEvent('sidebar:updated', { detail: { collapsed: this.collapsedValue } }))
    } catch (e) {
      // Ignore non-fatal dispatch errors in older browsers
    }
  }

  // Save user preference to backend
  savePreference() {
    const csrfToken = document.querySelector('[name="csrf-token"]')?.content

    if (!csrfToken) {
      logger.warn('CSRF token not found, cannot save sidebar preference')
      return
    }

    fetch('/users/update_sidebar_state', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken
      },
      body: JSON.stringify({ collapsed: this.collapsedValue })
    }).catch(error => {
      logger.error('Failed to save sidebar preference:', error)
    })
  }

  // Stimulus lifecycle: when collapsed value changes
  collapsedValueChanged() {
    this.updateToggleIcon()
    this.updateSidebarAwareElements()

    // Close any open floating submenus when toggling
    this.closeFloatingSubmenu()
  }

  // Helper method to check if a target exists
  hasTarget(targetName) {
    return this[`has${targetName.charAt(0).toUpperCase() + targetName.slice(1)}Target`]
  }

  // Cleanup on disconnect
  disconnect() {
    this.closeFloatingSubmenu()
  }
}
